/**
 * Provider-detail capability catalog (mock, CT-4180).
 *
 * Mirrors the prototype's `CAPABILITY_CATALOG` + per-integration `tools` /
 * `triggers` lists and `capabilities: ['tools','knowledge','triggers','channels']`.
 * Groups always rendered in that order; MCP is appended only when the provider
 * exposes granular MCP tools.
 */

const item = (id, name, description, extra = {}) => ({
  id,
  name,
  description,
  ...extra,
});

/** Prototype `INTEGRATIONS` / `CAPABILITY_CATALOG` per slug. */
const CATALOG = {
  slack: {
    // Prototype `#/integrations/slack` Capabilities: Tool + 3 Data Sync cards
    // (knowledge unsupported · 3 trigger events · routing channels). 2 instances.
    groups: ['tools', 'triggers', 'channels'],
    tools: [
      item('send_message', 'send_message', 'Send a message to a channel or user', {
        scope: 'chat:write',
      }),
      item('read_channel_history', 'read_channel_history', 'Read past messages in a channel', {
        scope: 'channels:history',
      }),
      item('create_channel', 'create_channel', 'Create a new channel', {
        scope: 'channels:manage',
      }),
      item('list_users', 'list_users', 'List workspace members', { scope: 'users:read' }),
      item('react_to_message', 'react_to_message', 'Add an emoji reaction to a message', {
        scope: 'reactions:write',
      }),
    ],
    triggers: [
      item('message.posted', 'message.posted', 'Fires on every new message'),
      item('app_mentioned', 'app_mentioned', 'Fires when the app is @-mentioned'),
      item('member_joined_channel', 'member_joined_channel', 'Fires when a member joins'),
    ],
  },
  salesforce: {
    groups: ['tools', 'knowledge'],
    tools: [
      item(
        'read_records',
        'read_records',
        'Query accounts, opportunities, contacts and custom objects.',
        { scope: 'records:read' },
      ),
      item(
        'update_record',
        'update_record',
        'Edit fields on existing records and trigger downstream rules.',
        { scope: 'records:write' },
      ),
    ],
  },
  github: {
    groups: ['tools', 'triggers'],
    tools: [
      item('create_issue', 'create_issue', 'Open new issues with title, body, labels and assignees.', {
        scope: 'issues:write',
      }),
      item('update_issue', 'update_issue', 'Edit title, body, labels, state, or close an issue.', {
        scope: 'issues:write',
      }),
      item('create_pr', 'create_pr', 'Open a PR with branch, title, description and reviewers.', {
        scope: 'pull_requests:write',
      }),
      item('merge_pr', 'merge_pr', 'Squash, merge or rebase a PR into its base branch.', {
        scope: 'contents:write',
      }),
    ],
    triggers: [
      item('pr_opened', 'pr_opened', 'Fires when a new PR is created in a watched repo.'),
      item('pr_merged', 'pr_merged', 'Fires when a PR is merged into its base branch.'),
      item('workflow_completed', 'workflow_completed', 'Fires when a GitHub Action workflow finishes.'),
    ],
    mcp: [
      item('gh.issues', 'github.issues', 'Create, update, search GitHub issues', { tool_count: 8 }),
      item('gh.repos', 'github.repos', 'Repository management — list, create, fork', {
        tool_count: 6,
      }),
      item('gh.actions', 'github.actions', 'Trigger and inspect GitHub Actions workflows', {
        tool_count: 5,
      }),
      item('gh.users', 'github.users', 'User and organization lookups', { tool_count: 4 }),
      item('gh.codespaces', 'github.codespaces', 'Manage GitHub Codespaces environments', {
        tool_count: 7,
      }),
    ],
  },
  zendesk: {
    groups: ['tools', 'triggers'],
    tools: [
      item('create_ticket', 'create_ticket', 'Open a new ticket with priority, assignee and description.', {
        scope: 'tickets:write',
      }),
      item('read_records', 'read_records', 'Query tickets and related records.', {
        scope: 'records:read',
      }),
      item('update_record', 'update_record', 'Edit fields on existing records.', {
        scope: 'records:write',
      }),
    ],
    triggers: [item('record_created', 'record_created', 'Fires when a new row is inserted.')],
  },
  notion: {
    groups: ['tools', 'knowledge'],
    tools: [
      item('search_documents', 'search_documents', 'Semantic search across indexed knowledge sources.', {
        scope: 'knowledge:read',
      }),
      item('read_records', 'read_records', 'Query pages and databases.', { scope: 'records:read' }),
      item('update_record', 'update_record', 'Edit fields on existing records.', {
        scope: 'records:write',
      }),
    ],
  },
  snowflake: {
    groups: ['tools', 'knowledge'],
    tools: [
      item('run_query', 'run_query', 'Execute SQL against your warehouse and return results.', {
        scope: 'warehouse:read',
      }),
      item('read_records', 'read_records', 'Query warehouse tables.', { scope: 'records:read' }),
    ],
  },
};

// Generic declared capabilities for providers not in the catalog above, so the
// Provider Detail Overview ("What you can do with it") always has content — the
// same "every connector declares something" intent as the prior flat catalog.
const DEFAULT_SPEC = {
  groups: ['tools'],
  tools: [
    item('read_records', 'read_records', 'Query records from this integration.', {
      scope: 'records:read',
    }),
    item('send_message', 'send_message', 'Trigger actions on your behalf.', {
      scope: 'actions:write',
    }),
  ],
};

const withActivity = (items, instanceCount) =>
  items.map((cap) => ({
    ...cap,
    active_instances: instanceCount,
    total_instances: instanceCount,
  }));

/**
 * Builds the snake_case capability payload for a provider detail mock.
 *
 * @param {string} slug
 * @param {number} instanceCount
 */
export const buildProviderCapabilities = (slug, instanceCount) => {
  const spec = CATALOG[String(slug ?? '').toLowerCase()] ?? DEFAULT_SPEC;
  const supported = new Set(spec?.groups ?? []);
  if (spec?.mcp?.length) supported.add('mcp');

  const capability_groups = {
    tools: {
      supported: supported.has('tools'),
      items: withActivity(spec?.tools ?? [], instanceCount),
    },
    knowledge: {
      supported: supported.has('knowledge'),
      items: [],
    },
    triggers: {
      supported: supported.has('triggers'),
      items: withActivity(spec?.triggers ?? [], instanceCount),
    },
    channels: {
      supported: supported.has('channels'),
      items: [],
    },
    mcp: {
      supported: supported.has('mcp'),
      items: withActivity(spec?.mcp ?? [], instanceCount),
    },
  };

  const capabilities = ['tools', 'triggers', 'mcp'].flatMap((key) =>
    capability_groups[key].items.map((cap) => ({
      id: cap.id,
      type: key === 'tools' ? 'tool' : key === 'mcp' ? 'mcp' : 'data_sync',
      group: key,
      label: cap.name,
      description: cap.description,
      scope: cap.scope,
      tool_count: cap.tool_count,
      active_instances: cap.active_instances,
      total_instances: cap.total_instances,
    })),
  );

  return { capability_groups, capabilities };
};
